import { 
  getDocument, 
  getDocuments, 
  createDocument, 
  setDocument, 
  updateDocument, 
  deleteDocument,
  uploadFile,
  uploadBase64,
  getFileURL,
  deleteFile,
  listFiles,
  subscribeToCollection,
  subscribeToDocument
} from './db_calls';
import { doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase.config';
import { getCollectionPath } from './utils/dbPaths';
import { getTenantId, getStoreId } from './services/userStoreCache';
import { buildReviewRequestPayloads } from './utils/reviewRequest';
import { useCustomer, useAuthStore, useUsersStore, useRoadCallStore } from './stores';
import { normalizeToE164, CALLBACK_WINDOW_MS } from './utils/roadCall';

// Helper function to capitalize names
const capitalizeName = (name) => {
  if (!name) return "";
  return name
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// ==================== ROAD CALLING (bridge dialer) ====================
// Ported from Warpspeed's src/db_calls_wrapper.js dbInitiateRoadCall /
// dbCancelRoadCall / dbSetCallExpectation / dbClearCallExpectation /
// dbListenToCallExpectations.

const roadCallInitiateFn = httpsCallable(functions, 'roadCallInitiate');
const roadCallCancelFn = httpsCallable(functions, 'roadCallCancel');

/**
 * Initiate an outbound bridged call: Twilio rings the logged-in user's
 * personal cell, then dials the customer with caller ID set to the road
 * number. Server also writes the call-expectations doc so a callback
 * within the window routes back to the same user.
 * @param {{customerPhone: string, customerName?: string, bookingId?: string}} params
 * @returns {Promise<Object>} { success, callSid, expiresAt, customerPhone } or { success: false, error }
 */
export const initiateRoadCall = async ({ customerPhone, customerName, bookingId } = {}) => {
  try {
    const tenantId = getTenantId();
    const storeId = getStoreId();
    if (!tenantId || !storeId) {
      return { success: false, error: 'tenantId/storeId not configured' };
    }
    const userId = useAuthStore.getState().selectedUser?.id;
    if (!userId) {
      return { success: false, error: 'No user logged in' };
    }
    const customerE164 = normalizeToE164(customerPhone);
    if (!customerE164) {
      return { success: false, error: 'Invalid customer phone' };
    }
    const result = await roadCallInitiateFn({
      tenantId,
      storeId,
      userId,
      customerPhone: customerE164,
      customerName: customerName || '',
      customerID: bookingId || '',
    });
    return result.data;
  } catch (error) {
    console.error('Error in initiateRoadCall:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Abort an in-flight outbound road call by SID. Also clears the matching
 * call-expectation doc (server is SID-guarded, so a stale cancel can't
 * clobber a newer expectation).
 * @param {{callSid: string, customerPhone?: string}} params
 */
export const cancelRoadCall = async ({ callSid, customerPhone } = {}) => {
  try {
    if (!callSid) return { success: false, error: 'callSid required' };
    const tenantId = getTenantId();
    const storeId = getStoreId();
    const result = await roadCallCancelFn({
      tenantId: tenantId || '',
      storeId: storeId || '',
      callSid,
      customerPhone: customerPhone || '',
    });
    return result.data;
  } catch (error) {
    console.error('Error in cancelRoadCall:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Manual "Allow Callbacks" — write an expectation doc directly so an
 * unsolicited inbound from this customer routes to the current user.
 * @param {{customerPhone: string, customerName?: string, bookingId?: string}} params
 */
export const setCallExpectation = async ({ customerPhone, customerName, bookingId } = {}) => {
  try {
    const tenantId = getTenantId();
    const storeId = getStoreId();
    if (!tenantId || !storeId) {
      return { success: false, error: 'tenantId/storeId not configured' };
    }
    const currentUser = useUsersStore.getState().currentUser;
    const userId = useAuthStore.getState().selectedUser?.id;
    const userCell = normalizeToE164(currentUser?.phone);
    if (!userId || !userCell) {
      return { success: false, error: 'Your account has no cell number saved in User Settings.' };
    }
    const customerE164 = normalizeToE164(customerPhone);
    if (!customerE164) {
      return { success: false, error: 'Invalid customer phone' };
    }
    const now = Date.now();
    const expectation = {
      userID: userId,
      userCell,
      customerPhone: customerE164,
      customerName: customerName || '',
      customerID: bookingId || '',
      setBy: 'manual',
      setAt: now,
      expiresAt: now + CALLBACK_WINDOW_MS,
    };
    await setDocument(getCollectionPath('call-expectations'), customerE164, expectation, true);
    return { success: true, expectation };
  } catch (error) {
    console.error('Error in setCallExpectation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancel an expectation (manual clear, e.g. from the callback banner).
 * @param {string} customerPhone
 */
export const clearCallExpectation = async (customerPhone) => {
  try {
    const customerE164 = normalizeToE164(customerPhone);
    if (!customerE164) {
      return { success: false, error: 'Invalid customer phone' };
    }
    await deleteDocument(getCollectionPath('call-expectations'), customerE164);
    return { success: true };
  } catch (error) {
    console.error('Error in clearCallExpectation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Live listener for the call-expectations collection, feeding useRoadCallStore.
 * Caller (App.js) owns the subscribe/unsubscribe lifecycle.
 * @returns {Function} Unsubscribe function
 */
export const listenToCallExpectations = () => {
  return subscribeToCollection(getCollectionPath('call-expectations'), (expectations) => {
    useRoadCallStore.getState().setCallExpectations(expectations || []);
  });
};

// ==================== USER OPERATIONS ====================

/**
 * Get user profile by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile data
 */
export const getUserProfile = async (userId) => {
  return await getDocument('users', userId);
};

/**
 * Create or update user profile
 * @param {string} userId - User ID
 * @param {Object} profileData - User profile data
 * @returns {Promise<void>}
 */
export const saveUserProfile = async (userId, profileData) => {
  return await setDocument('users', userId, profileData, true);
};

/**
 * Update user profile fields
 * @param {string} userId - User ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (userId, updates) => {
  return await updateDocument('users', userId, updates);
};

// ==================== PHOTO OPERATIONS ====================

/**
 * Upload a photo and save metadata to Firestore
 * @param {string} userId - User ID
 * @param {string} base64Photo - Base64 encoded photo
 * @param {Object} metadata - Additional metadata (title, description, etc.)
 * @returns {Promise<Object>} Photo document with ID and URL
 */
export const savePhoto = async (userId, base64Photo, metadata = {}) => {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const photoId = `photo_${timestamp}`;
    const storagePath = `photos/${userId}/${photoId}.jpg`;
    
    // Upload photo to Cloud Storage
    const photoURL = await uploadBase64(storagePath, base64Photo, 'data_url', {
      contentType: 'image/jpeg',
      customMetadata: {
        userId: userId,
        uploadedAt: new Date().toISOString()
      }
    });
    
    // Save photo metadata to Firestore
    const photoData = {
      userId,
      photoURL,
      storagePath,
      ...metadata,
      uploadedAt: new Date(),
    };
    
    const docId = await createDocument('photos', photoData);
    
    return {
      id: docId,
      ...photoData,
      photoURL
    };
  } catch (error) {
    console.error('Error saving photo:', error);
    throw error;
  }
};

/**
 * Get a single photo by ID
 * @param {string} photoId - Photo document ID
 * @returns {Promise<Object|null>} Photo data
 */
export const getPhoto = async (photoId) => {
  return await getDocument('photos', photoId);
};

/**
 * Get all photos for a user
 * @param {string} userId - User ID
 * @param {number} limitCount - Maximum number of photos to retrieve
 * @returns {Promise<Array>} Array of photo documents
 */
export const getUserPhotos = async (userId, limitCount = 50) => {
  return await getDocuments('photos', {
    where: {
      field: 'userId',
      operator: '==',
      value: userId
    },
    orderBy: {
      field: 'uploadedAt',
      direction: 'desc'
    },
    limit: limitCount
  });
};

/**
 * Update photo metadata
 * @param {string} photoId - Photo document ID
 * @param {Object} updates - Fields to update (title, description, etc.)
 * @returns {Promise<void>}
 */
export const updatePhoto = async (photoId, updates) => {
  return await updateDocument('photos', photoId, updates);
};

/**
 * Delete a photo and its file from storage
 * @param {string} photoId - Photo document ID
 * @returns {Promise<void>}
 */
export const deletePhoto = async (photoId) => {
  try {
    // Get photo document to retrieve storage path
    const photoData = await getDocument('photos', photoId);
    
    if (!photoData) {
      throw new Error('Photo not found');
    }
    
    // Delete file from Cloud Storage
    if (photoData.storagePath) {
      await deleteFile(photoData.storagePath);
    }
    
    // Delete document from Firestore
    await deleteDocument('photos', photoId);
    
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

// ==================== ALBUM/COLLECTION OPERATIONS ====================

/**
 * Create a new album/collection
 * @param {string} userId - User ID
 * @param {Object} albumData - Album data (name, description, etc.)
 * @returns {Promise<string>} Album document ID
 */
export const createAlbum = async (userId, albumData) => {
  return await createDocument('albums', {
    userId,
    ...albumData,
    photoCount: 0
  });
};

/**
 * Get user's albums
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of albums
 */
export const getUserAlbums = async (userId) => {
  return await getDocuments('albums', {
    where: {
      field: 'userId',
      operator: '==',
      value: userId
    },
    orderBy: {
      field: 'createdAt',
      direction: 'desc'
    }
  });
};

/**
 * Update album data
 * @param {string} albumId - Album document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateAlbum = async (albumId, updates) => {
  return await updateDocument('albums', albumId, updates);
};

/**
 * Delete an album
 * @param {string} albumId - Album document ID
 * @returns {Promise<void>}
 */
export const deleteAlbum = async (albumId) => {
  return await deleteDocument('albums', albumId);
};

// ==================== SETTINGS OPERATIONS ====================

/**
 * Get user settings
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User settings
 */
export const getUserSettings = async (userId) => {
  const { getSettingsPath } = await import('./utils/dbPaths');
  const settingsPath = getSettingsPath();
  return await getDocument(settingsPath, userId);
};

/**
 * Save user settings
 * @param {string} userId - User ID
 * @param {Object} settings - Settings data
 * @returns {Promise<void>}
 */
export const saveUserSettings = async (userId, settings) => {
  const { getSettingsPath } = await import('./utils/dbPaths');
  const settingsPath = getSettingsPath();
  return await setDocument(settingsPath, userId, settings, true);
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Check if a document exists
 * @param {string} collectionName - Collection name
 * @param {string} documentId - Document ID
 * @returns {Promise<boolean>} True if document exists
 */
export const documentExists = async (collectionName, documentId) => {
  const doc = await getDocument(collectionName, documentId);
  return doc !== null;
};

/**
 * Get photo download URL from storage path
 * @param {string} storagePath - Storage path
 * @returns {Promise<string>} Download URL
 */
export const getPhotoURL = async (storagePath) => {
  return await getFileURL(storagePath);
};

// ==================== CUSTOMER OPERATIONS ====================

/**
 * Subscribe to real-time updates from customers collection
 * Automatically updates the Zustand store when customers change
 * @param {Object} options - Query options (orderBy, limit, etc.)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCustomers = (options = {}) => {
  const defaultOptions = {
    orderBy: {
      field: "createdAt",
      direction: "desc",
    },
    ...options,
  };


  const customersPath = getCollectionPath("customers");
  const unsubscribe = subscribeToCollection(
    customersPath,
    (customers, error) => {
      if (error) {
        console.error("❌ Error in customers subscription:", error);
        return;
      }

      // Add computed fullName to each customer
      const customersWithFullName = customers.map((customer) => ({
        ...customer,
        fullName: `${customer.firstName || ""} ${
          customer.lastName || ""
        }`.trim(),
      }));

      // Update Zustand store with the latest customers
      const setCustomers = useCustomer.getState().setCustomers;
      setCustomers(customersWithFullName);
    },
    defaultOptions
  );

  return unsubscribe;
};

/**
 * Create a new customer
 * @param {Object} customerData - Customer data
 * @returns {Promise<string>} Customer document ID
 */
export const createCustomer = async (customerData) => {
  const customersPath = getCollectionPath("customers");
  return await createDocument(customersPath, customerData);
};

/**
 * Get a single customer by ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object|null>} Customer data
 */
export const getCustomer = async (customerId) => {
  const customersPath = getCollectionPath("customers");
  return await getDocument(customersPath, customerId);
};

/**
 * Get all customers (one-time fetch)
 * @param {number} limitCount - Maximum number of customers
 * @returns {Promise<Array>} Array of customers
 */
export const getCustomers = async (limitCount = 100) => {
  const customersPath = getCollectionPath("customers");
  return await getDocuments(customersPath, {
    orderBy: {
      field: "createdAt",
      direction: "desc",
    },
    limit: limitCount,
  });
};

/**
 * Update a customer
 * @param {string} customerId - Customer ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateCustomer = async (customerId, updates) => {
  const customersPath = getCollectionPath("customers");
  return await updateDocument(customersPath, customerId, updates);
};

/**
 * Delete a customer
 * @param {string} customerId - Customer ID
 * @returns {Promise<void>}
 */
export const deleteCustomer = async (customerId) => {
  const customersPath = getCollectionPath("customers");
  return await deleteDocument(customersPath, customerId);
};

/**
 * Get customer thumbnails from Cloud Storage
 * @param {string} customerPhone - Customer phone number
 * @param {number} limit - Maximum number of thumbnails to retrieve
 * @returns {Promise<Array>} Array of thumbnail objects with URLs
 */
export const getCustomerThumbnails = async (customerPhone, limit = 10) => {
  try {
    const path = `customers/${customerPhone}/photos`;

    const allFiles = await listFiles(path);

    // Filter for thumbnails only (thumb_ prefix)
    const thumbnails = allFiles
      .filter((file) => file.name.startsWith("thumb_"))
      .sort((a, b) => {
        // Extract timestamp from filename (thumb_1234567890.jpg)
        const timestampA = parseInt(
          a.name.replace("thumb_", "").replace(".jpg", "")
        );
        const timestampB = parseInt(
          b.name.replace("thumb_", "").replace(".jpg", "")
        );
        return timestampB - timestampA; // Newest first
      })
      .slice(0, limit) // Limit to requested number
      .map((thumbnail) => {
        // Get corresponding full-size image path
        const fullSizePath = thumbnail.fullPath.replace("thumb_", "full_");

        return {
          ...thumbnail,
          thumbnailUrl: thumbnail.url,
          fullSizePath: fullSizePath,
        };
      });

    return thumbnails;
  } catch (error) {
    console.error("Error fetching customer thumbnails:", error);
    return [];
  }
};

/**
 * Subscribe to bookings collection
 * @param {Function} callback - Optional callback for when data changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToBookings = (callback) => {

  const bookingsPath = getCollectionPath("bookings");
  const unsubscribe = subscribeToCollection(
    bookingsPath,
    (bookings, error) => {
      if (error) {
        console.error("❌ Error in bookings subscription:", error);
        if (callback) callback([], error);
        return;
      }

      // Add computed fullName to each booking (with capitalized names)
      const bookingsWithFullName = bookings.map((booking) => ({
        ...booking,
        fullName: `${capitalizeName(booking.firstName || "")} ${capitalizeName(booking.lastName || "")}`.trim(),
      }));

      if (callback) {
        callback(bookingsWithFullName);
      }
    },
    {
      orderBy: {
        field: "createdAt",
        direction: "desc",
      },
    }
  );

  return unsubscribe;
};

/**
 * Subscribe to messages for a specific customer
 * @param {string} customerPhone - Customer phone number (10 digits)
 * @param {Function} callback - Callback function to handle message updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMessages = (customerPhone, callback) => {

  // Subscribe to messages subcollection using tenant/store path
  const conversationsPath = getCollectionPath("conversations");
  const messagesPath = `${conversationsPath}/${customerPhone}/messages`;
  const unsubscribe = subscribeToCollection(
    messagesPath,
    (messages, error) => {
      if (error) {
        console.error("❌ Error in messages subscription:", error);
        if (callback) callback([], error);
        return;
      }


      // Sort messages by time (oldest first for chat display)
      // Note: outbound messages have sentAt, inbound messages have receivedAt
      const sortedMessages = messages.sort((a, b) => {
        const timeA = a.sentAt || a.receivedAt;
        const timeB = b.sentAt || b.receivedAt;

        if (!timeA || !timeB) return 0;

        const dateA = timeA.toDate ? timeA.toDate() : new Date(timeA);
        const dateB = timeB.toDate ? timeB.toDate() : new Date(timeB);

        return dateA - dateB;
      });

      if (callback) {
        callback(sortedMessages);
      }
    }
    // No orderBy - we'll sort client-side to handle both sentAt and receivedAt
  );

  return unsubscribe;
};

/**
 * Subscribe to all conversations (for inbox view)
 * @param {Function} callback - Callback function to handle conversation updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToConversations = (callback) => {

  const conversationsPath = getCollectionPath("conversations");
  const unsubscribe = subscribeToCollection(
    conversationsPath,
    (conversations, error) => {
      if (error) {
        console.error("❌ Error in conversations subscription:", error);
        if (callback) callback([], error);
        return;
      }


      // Sort by last message time (newest first)
      const sortedConversations = conversations.sort((a, b) => {
        const timeA = a.lastMessageAt;
        const timeB = b.lastMessageAt;

        if (!timeA || !timeB) return 0;

        const dateA = timeA.toDate ? timeA.toDate() : new Date(timeA);
        const dateB = timeB.toDate ? timeB.toDate() : new Date(timeB);

        return dateB - dateA; // Newest first
      });

      if (callback) {
        callback(sortedConversations);
      }
    },
    {
      orderBy: {
        field: "lastMessageAt",
        direction: "desc",
      },
    }
  );

  return unsubscribe;
};

/**
 * Get unread message count across all conversations
 * @returns {Promise<number>} Total unread count
 */
export const getTotalUnreadCount = async () => {
  try {
    const conversationsPath = getCollectionPath("conversations");
    const conversations = await getDocuments(conversationsPath);
    const totalUnread = conversations.reduce((sum, conv) => {
      return sum + (conv.unreadCount || 0);
    }, 0);
    return totalUnread;
  } catch (error) {
    console.error("Error getting unread count:", error);
    return 0;
  }
};

// ==================== LOCK OPERATIONS ====================

/**
 * Create a new combination lock
 * @param {Object} lockData - Lock data {combo, name}
 * @returns {Promise<string>} Generated lock ID
 */
export const createLock = async (lockData) => {

  try {
    // Validate required fields
    if (!lockData.combo || !lockData.tagNumber) {
      throw new Error("Missing required fields: combo and tagNumber are required");
    }

    // Prepare lock data - save exact tag number as name
    const lock = {
      combo: lockData.combo,
      combination: lockData.combo, // Also save as combination for consistency
      tagNumber: lockData.tagNumber,
      name: lockData.name || lockData.tagNumber.toString(), // Use exact tag number as name
      createdAt: new Date(),
      updatedAt: new Date(),
      isAvailable: true, // Track if lock is currently assigned
    };

    // Create document with auto-generated ID
    const locksPath = getCollectionPath("locks");
    const lockId = await createDocument(locksPath, lock);

    return lockId;
  } catch (error) {
    console.error("❌ Error creating lock:", error);
    throw error;
  }
};

/**
 * Get a lock by ID
 * @param {string} lockId - Lock ID
 * @returns {Promise<Object|null>} Lock data with id field
 */
export const getLock = async (lockId) => {

  try {
    const locksPath = getCollectionPath("locks");
    const lock = await getDocument(locksPath, lockId);

    if (lock) {
      return { id: lockId, ...lock };
    }

    return null;
  } catch (error) {
    console.error("❌ Error getting lock:", error);
    throw error;
  }
};

/**
 * Get all locks
 * @param {Object} options - Query options (orderBy, limit, etc.)
 * @returns {Promise<Array>} Array of locks with id field
 */
export const getAllLocks = async (options = {}) => {

  try {
    const locksPath = getCollectionPath("locks");
    const locks = await getDocuments(locksPath, options);

    return locks;
  } catch (error) {
    console.error("❌ Error getting locks:", error);
    throw error;
  }
};

/**
 * Update lock information
 * @param {string} lockId - Lock ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateLock = async (lockId, updates) => {

  try {
    const locksPath = getCollectionPath("locks");
    await updateDocument(locksPath, lockId, updates);
  } catch (error) {
    console.error("❌ Error updating lock:", error);
    throw error;
  }
};

/**
 * Delete a lock
 * @param {string} lockId - Lock ID
 * @returns {Promise<void>}
 */
export const deleteLock = async (lockId) => {

  try {
    const locksPath = getCollectionPath("locks");
    await deleteDocument(locksPath, lockId);
  } catch (error) {
    console.error("❌ Error deleting lock:", error);
    throw error;
  }
};

/**
 * Subscribe to locks collection for real-time updates
 * @param {Function} callback - Callback function to handle lock updates
 * @param {Object} options - Query options (orderBy, limit, etc.)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToLocks = (callback, options = {}) => {

  const locksPath = getCollectionPath("locks");
  const unsubscribe = subscribeToCollection(
    locksPath,
    (locks, error) => {
      if (error) {
        console.error("❌ Error in locks subscription:", error);
        if (callback) callback([], error);
        return;
      }


      if (callback) {
        callback(locks);
      }
    },
    options
  );

  return unsubscribe;
};

/**
 * Get available (unassigned) locks
 * @returns {Promise<Array>} Array of available locks
 */
export const getAvailableLocks = async () => {

  try {
    const locksPath = getCollectionPath("locks");
    const locks = await getDocuments(locksPath);
    const available = locks.filter((lock) => lock.isAvailable !== false);

    return available;
  } catch (error) {
    console.error("❌ Error getting available locks:", error);
    throw error;
  }
};

/**
 * Assign lock to a rental
 * @param {string} lockId - Lock ID
 * @param {string} rentalId - Rental ID
 * @param {string} customerName - Customer name
 * @returns {Promise<void>}
 */
export const assignLock = async (lockId, rentalId, customerName) => {

  try {
    const locksPath = getCollectionPath("locks");
    await updateDocument(locksPath, lockId, {
      isAvailable: false,
      assignedTo: rentalId,
      assignedCustomer: customerName,
      assignedAt: new Date(),
    });

  } catch (error) {
    console.error("❌ Error assigning lock:", error);
    throw error;
  }
};

/**
 * Release lock (make available again)
 * @param {string} lockId - Lock ID
 * @returns {Promise<void>}
 */
export const releaseLock = async (lockId) => {

  try {
    const locksPath = getCollectionPath("locks");
    await updateDocument(locksPath, lockId, {
      isAvailable: true,
      assignedTo: null,
      assignedCustomer: null,
      releasedAt: new Date(),
    });

  } catch (error) {
    console.error("❌ Error releasing lock:", error);
    throw error;
  }
};

// ==================== REVIEW REQUEST OPERATIONS ====================

/**
 * Schedule a 5-star review request (SMS and/or email) for a completed booking.
 * Writes ONE doc to the top-level `scheduledReviewRequests` collection (NOT tenant-scoped —
 * the cron sweeps this collection directly) and stamps `reviewRequestLog/{contactKey}` so the
 * customer isn't re-asked within the admin-configured suppression window.
 * @param {object} booking
 * @param {object} settings - the Zustand `settings` object (reads settings.reviewRequest)
 * @param {{userId?: string, firstName?: string, lastName?: string}} userMeta - operator who tapped "Yes"
 * @returns {Promise<{ success: boolean, id?: string, error?: string }>}
 */
export const scheduleReviewRequest = async (booking, settings, userMeta) => {
  try {
    const tenantId = getTenantId();
    const storeId = getStoreId();
    if (!tenantId || !storeId) {
      throw new Error("No tenant/store context");
    }

    const payloads = buildReviewRequestPayloads(booking, settings);
    if (!payloads) {
      throw new Error("No review URL configured or no contact info on booking");
    }

    const rr = settings?.reviewRequest || {};
    const delayHours = Math.min(72, Math.max(1, Number(rr.delayHours) || 4));
    const delayMs = delayHours * 3600000;
    const nowMillis = Date.now();

    const id = crypto.randomUUID();
    const docData = {
      id,
      type: "reviewRequest",
      tenantId,
      storeId,
      bookingId: booking?.id || booking?.bookingId || "",
      customerFirst: booking?.firstName || "",
      customerLast: booking?.lastName || "",
      phone: payloads.phone,
      email: payloads.email,
      smsBody: payloads.smsBody,
      emailSubject: payloads.emailSubject,
      emailHtml: payloads.emailHtml,
      sendAtMillis: nowMillis + delayMs,
      status: "pending",
      sms: { status: "", messageId: "", errorMessage: "" },
      email: { status: "", messageId: "", errorMessage: "" },
      createdAtMillis: nowMillis,
      createdBy: userMeta?.userId || "",
      sentAtMillis: 0,
    };

    await setDoc(doc(db, "scheduledReviewRequests", id), docData);

    const contactKey = payloads.phone || payloads.email.toLowerCase();
    const logPath = getCollectionPath("reviewRequestLog");
    await setDoc(
      doc(db, logPath, contactKey),
      { lastAskedMillis: nowMillis, lastScheduledId: id },
      { merge: true }
    );

    return { success: true, id };
  } catch (error) {
    console.error("❌ Error scheduling review request:", error);
    return { success: false, error: error.message };
  }
};

